/** Stock Out Report: what left the depot between 09:00 on the first day and 17:00 on the last, by department. Unchanged products are left off. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, FileBarChart2, Loader2, PackageMinus, PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { BRANDS } from '@/config/stockBrands';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const monday = (d) => { const x = new Date(d); const k = (x.getDay() + 6) % 7; x.setDate(x.getDate() - k); return x; };
const QUICK = [
  ['today', 'Today', () => { const n = new Date(); return [n, n]; }],
  ['yesterday', 'Yesterday', () => { const y = addDays(new Date(), -1); return [y, y]; }],
  ['this_week', 'This week', () => { const m = monday(new Date()); return [m, addDays(m, 6)]; }],
  ['last_week', 'Last week', () => { const m = addDays(monday(new Date()), -7); return [m, addDays(m, 6)]; }],
  ['this_month', 'This month', () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth(), 1), new Date(n.getFullYear(), n.getMonth() + 1, 0)]; }],
  ['last_month', 'Last month', () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 0)]; }],
];
const fmtD = (s) => new Date(`${s}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

export default function StockOutReport() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const start = params.get('start') || iso(monday(new Date()));
  const end = params.get('end') || iso(new Date());
  const brand = params.get('brand') || 'all';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const setP = (patch) => setParams((p) => { const n = new URLSearchParams(p); Object.entries(patch).forEach(([k, v]) => n.set(k, v)); return n; });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/stock/reports/out?start=${start}&end=${end}&brand=${brand}`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not build the report');
      setData(d);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [start, end, brand]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const m = new Map();
    (data?.rows || []).forEach((r) => { const k = `${r.brand_label} · ${r.category}`; if (!m.has(k)) m.set(k, { brand: r.brand, label: k, rows: [] }); m.get(k).rows.push(r); });
    return [...m.values()];
  }, [data]);
  const colour = (b) => BRANDS.find((x) => x.key === b)?.color || '#4B5563';
  const csvUrl = `${API_URL}/api/stock/reports/out.csv?start=${start}&end=${end}&brand=${brand}`;
  const download = async () => {
    const r = await fetch(csvUrl, { credentials: 'include' });
    if (!r.ok) return toast.error('Export failed');
    const blob = await r.blob();
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `stock_out_${brand}_${start}_to_${end}.csv` });
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  };
  const t = data?.totals;

  return (
    <div className="min-h-screen bg-[#F4F1EA]" data-testid="stock-out-report">
      <div className="text-white" style={{ background: '#0B0B0E' }}>
        <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/staff/stock')} className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to stock" data-testid="sor-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2"><FileBarChart2 className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4A24B' }} /> Stock Out Report</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Stock at 09:00 on the first day vs 17:00 on the last — only products that moved are listed.</p>
          </div>
          <Button onClick={download} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" disabled={!data?.rows?.length} data-testid="sor-export"><Download className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Download CSV</span></Button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-4 space-y-4">
        <div className="bg-white rounded-2xl border border-stone-200 p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="sor-quick">
            {QUICK.map(([k, label, fn]) => { const [a, b] = fn().map(iso); const on = a === start && b === end; return (
              <button key={k} type="button" onClick={() => setP({ start: a, end: b })} data-testid={`sor-quick-${k}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${on ? 'bg-[#0B0B0E] border-[#0B0B0E] text-white' : 'bg-white border-stone-300 text-stone-700 hover:border-stone-500'}`}>{label}</button>); })}
            <div className="ml-auto flex items-center gap-2 text-sm">
              <input type="date" value={start} max={end} onChange={(e) => setP({ start: e.target.value })} className="h-9 rounded-md border border-stone-300 px-2 text-sm" data-testid="sor-start" />
              <span className="text-stone-400">to</span>
              <input type="date" value={end} min={start} onChange={(e) => setP({ end: e.target.value })} className="h-9 rounded-md border border-stone-300 px-2 text-sm" data-testid="sor-end" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="sor-brands">
            <span className="text-xs font-bold uppercase tracking-wide text-stone-500 mr-1">Department</span>
            {[{ key: 'all', label: 'All', color: '#0B0B0E', ink: '#fff' }, ...BRANDS].map((b) => { const on = brand === b.key; return (
              <button key={b.key} type="button" onClick={() => setP({ brand: b.key })} data-testid={`sor-brand-${b.key}`}
                className="rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors" style={on ? { background: b.color, borderColor: b.color, color: b.ink } : { background: '#fff', borderColor: '#d6d3d1', color: '#44403c' }}>{b.label}</button>); })}
          </div>
        </div>

        {data && (
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="sor-summary">
            <span className="font-bold text-stone-800">{data.brand_label} · {fmtD(start)} 09:00 → {fmtD(end)} 17:00</span>
            <span className="rounded-full bg-white border px-2.5 py-1 font-semibold text-stone-700">{t.lines} product{t.lines === 1 ? '' : 's'} moved</span>
            <span className="rounded-full bg-rose-50 border border-rose-200 px-2.5 py-1 font-semibold text-rose-800 inline-flex items-center gap-1" data-testid="sor-units-out"><PackageMinus className="w-3 h-3" /> {t.units_out} units out</span>
            {t.units_delivered > 0 && <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 font-semibold text-emerald-800 inline-flex items-center gap-1"><PackagePlus className="w-3 h-3" /> {t.units_delivered} delivered in</span>}
            {t.orders > 0 && <span className="rounded-full bg-white border px-2.5 py-1 font-semibold text-stone-700">{t.orders} order line{t.orders === 1 ? '' : 's'}</span>}
            {loading && <Loader2 className="w-4 h-4 animate-spin text-stone-400" />}
          </div>
        )}

        {data && !data.rows.length && <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-sm text-stone-500" data-testid="sor-empty">No stock moved for {data.brand_label.toLowerCase()} between these times — nothing sold, nothing delivered.</div>}

        {groups.map((g) => (
          <div key={g.label} className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="sor-group">
            <div className="px-4 py-2 text-xs font-bold text-white flex items-center gap-2" style={{ background: colour(g.brand) }}>{g.label}<span className="ml-auto font-semibold opacity-80">{g.rows.reduce((s, r) => s + r.out, 0)} out</span></div>
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wide text-stone-500 border-b">
                <th className="text-left px-4 py-2 font-bold">Product</th>
                <th className="text-right px-3 py-2 font-bold whitespace-nowrap">Start 09:00</th>
                <th className="text-right px-3 py-2 font-bold whitespace-nowrap">End 17:00</th>
                <th className="text-right px-3 py-2 font-bold">Out</th>
                <th className="text-right px-3 py-2 font-bold hidden sm:table-cell">Delivered</th>
                <th className="text-right px-3 py-2 font-bold hidden sm:table-cell">Adjust</th>
                <th className="text-right px-4 py-2 font-bold">Net</th>
              </tr></thead>
              <tbody className="divide-y">
                {g.rows.map((r) => (
                  <tr key={r.product_id} className="hover:bg-stone-50" data-testid={`sor-row-${r.product_id}`}>
                    <td className="px-4 py-2 font-semibold text-stone-900">{r.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-stone-600">{r.start_qty}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-stone-600">{r.end_qty}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-rose-700">{r.out ? `−${r.out}` : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700 hidden sm:table-cell">{r.delivered ? `+${r.delivered}` : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-stone-500 hidden sm:table-cell">{r.adjustments ? (r.adjustments > 0 ? `+${r.adjustments}` : r.adjustments) : '—'}</td>
                    <td className={`px-4 py-2 text-right tabular-nums font-bold ${r.net < 0 ? 'text-rose-700' : r.net > 0 ? 'text-emerald-700' : 'text-stone-500'}`}>{r.net > 0 ? `+${r.net}` : r.net}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
